import React, { useEffect, useMemo, useRef, useState } from 'react';
import 'react-datepicker/dist/react-datepicker.css';
import Loader from 'react-loader-spinner';
import { styled } from 'styled-components';
import { Icon } from '../../concepts/resources/Icon';
import { Button, Cell, ColorSelector, Colors, Float, Grid, Input, Menu, Select, Spinner, Toggle, TypeaheadInput, TypeaheadItem, remInPx } from '../../ui';
import { Badge } from '../../ui/Badge';
import { BigButton } from '../../ui/BigButton';
import { ButtonToggle } from '../../ui/ButtonToggle';
import { Check } from '../../ui/Check';
import { Checkbox } from '../../ui/Checkbox';
import { ColorButton } from '../../ui/ColorButton';
import { DatePopper } from '../../ui/DatePopper';
import { BetterDropButton, DropButton } from '../../ui/DropButton';
import { GridButton } from '../../ui/GridButton';
import { HeaderButton } from '../../ui/HeaderButton';
import { InfoView } from '../../ui/InfoView';
import { InlineHelp } from '../../ui/InlineHelp';
import { LoadingBar } from '../../ui/LoadingBar';
import { LoadingGuard } from '../../ui/LoadingGuard';
import { MenuButton } from '../../ui/MenuButton';
import { Notice } from '../../ui/Notice';
import { PaymentCard } from '../../ui/PaymentCard';
import { PillEditor, PillableItem } from '../../ui/PillEditor';
import { PopArrow } from '../../ui/PopArrow';
import { PopupTabs } from '../../ui/PopupTabs';
import { ResultInfo } from '../../ui/ResultInfo';
import { Skeleton } from '../../ui/Skeleton';
import { Slider } from '../../ui/Slider';
import { SliderWithInput } from '../../ui/SliderWithInput';
import { StarRating } from '../../ui/StarRating';
import { Table } from '../../ui/Table';
import { TextButton } from '../../ui/TextButton';
import { TimePopper } from '../../ui/TimePopper';
import { Tip } from '../../ui/Tip';
import { VerticalTabSet } from '../../ui/VerticalTabSet';
import { Scroller } from '../boards/Scroller';
import { DeprecatedSpinner as CommonSpinner } from '../common/Spinner';
import { ButtonStrip, Copy, Flexer, Title } from '../dashboard/elements/LayoutElements';

const HeaderButtonDemo: React.FC = () => {
    const [active, setActive] = useState(false);

    return <HeaderButton active={active} onClick={() => setActive}>
        <span>Header Button</span>
    </HeaderButton>;
};

const ButtonDemo: React.FC = () => {
    return <a id="buttons-section">
        <Flexer flex='column' gap='0.5'>
            <Title size='xl'>Buttons:</Title>
            <HeaderButtonDemo />
            <Flexer flex='row' align='center' justify='space-around'>
                <Flexer flex='column' align='center' justify='space-between' height='25'>
                    <Button>Regular</Button>
                    <Button size='xs'>XS</Button>
                    <Button size='sm'>SM</Button>
                    <Button size='md'>MD</Button>
                    <Button size='lg'>LG</Button>
                    <Button size='xl'>XL</Button>
                    <Button size='xxl'>XXL</Button>
                </Flexer>
                <Flexer flex='column' align='center' justify='space-between' height='25'>
                    <Button variant='primary'>Primary</Button>
                    <Button variant='positive'>Positive</Button>
                    <Button variant='negative'>Negative</Button>
                    <Button variant='mystery'>Mystery</Button>
                    <Button variant='observe'>Observe</Button>
                    <Button variant='common'>Common</Button>
                    <Button variant='text'>Text</Button>
                    <Button disabled={true}>Disabled</Button>
                </Flexer>
                <Flexer flex='column' align='center' justify='space-between' height='25'>
                    <DropButton>
                        <div>Drop Button</div>
                    </DropButton>
                    <DropButton active={true}>
                        <div>Active</div>
                    </DropButton>
                    <DropButton disabled={true}>
                        <div>Disabled</div>
                    </DropButton>
                    <DropButton overflow="0" padding=".125">
                        <Icon size="1.25" src="deqo://icons/hero/check" />
                    </DropButton>
                    <BetterDropButton>
                        <div>Better DB</div>
                    </BetterDropButton>
                    <BetterDropButton variant='primary'>
                        <div>Primary</div>
                    </BetterDropButton>
                    <BetterDropButton variant='inverted'>
                        <div>Inverted</div>
                    </BetterDropButton>
                    <BetterDropButton disabled={true}>
                        <div>Disabled</div>
                    </BetterDropButton>
                </Flexer>
                <Flexer flex='column' align='center' justify='space-between' height='25'>
                    <BigButton>
                        <BigButton.Inner expand>
                            <Title size="md">Big Button</Title>
                            <Copy>Lorem ipsum dolor sit amet.</Copy>
                        </BigButton.Inner>
                    </BigButton>
                    <BigButton>
                        <BigButton.Icon color={Colors.primary}>
                            <Icon size="1.5" src="deqo://icons/hero/check" />
                        </BigButton.Icon>
                        <BigButton.Inner>
                            <Title size="sm">Title</Title>
                        </BigButton.Inner>
                    </BigButton>
                    <BigButton>
                        <BigButton.Icon color={Colors.positive}>
                            <Icon size="1.5" src="deqo://icons/hero/cash" />
                        </BigButton.Icon>
                        <BigButton.Inner>
                            <Title size="sm">Title</Title>
                        </BigButton.Inner>
                    </BigButton>
                    <BigButton>
                        <BigButton.Icon color={Colors.negative}>
                            <Icon size="1.5" src="deqo://icons/hero/emoji-sad" />
                        </BigButton.Icon>
                        <BigButton.Inner>
                            <Title size="sm">Title</Title>
                        </BigButton.Inner>
                    </BigButton>
                </Flexer>
                <Flexer flex='column' align='center' justify='space-between' height='25'>
                    <TextButton>Text Button</TextButton>
                    <TextButton underline>Underline</TextButton>
                    <TextButton size='2'>Large</TextButton>
                    <TextButton color={Colors.negative}>Red</TextButton>
                </Flexer>
            </Flexer>
        </Flexer>
    </a>;
};

const ButtonToggleDemo: React.FC = () => {
    const options = [
        { label: 'Option 1', value: '1' },
        { label: 'Option 2', value: '2' },
        { label: 'Option 3', value: '3' },
    ];

    const [selected, setSelected] = useState(options[0].value);

    return <a id='button-toggle-section'>
        <Flexer flex='column' gap='0.5'>
            <Title size='xl'>Button Toggle:</Title>
            <Flexer flex='row' align='center' justify='space-around'>
                <ButtonToggle size='sm' options={options} selected={selected} onChange={setSelected} />
                <ButtonToggle size='md' options={options} selected={selected} onChange={setSelected} />
                <ButtonToggle size='lg' options={options} selected={selected} onChange={setSelected} />
            </Flexer>
        </Flexer>
    </a>;
};

const StarRatingDemo: React.FC = () => {
    const [rating, setRating] = useState(3);

    return <a id='star-rating-section'>
        <Flexer flex='column' gap='0.5'>
            <Title size='xl'>Star Rating:</Title>
            <div style={{ backgroundColor: Colors.primary, borderRadius: '0.25rem', padding: '0.25rem', width: '20rem' }}>
                <StarRating size={5} initialValue={rating} onSelect={setRating} />
            </div>
        </Flexer>
    </a>;
};

const BadgeDemo: React.FC = () => {
    return <a id='badges-section'>
        <Flexer flex='column' gap='0.5'>
            <Title size='xl'>Badges:</Title>
            <Flexer flex='column' align='center' justify='space-between' height='25'>
                <Badge color={Colors.text}>Text</Badge>
                <Badge color={Colors.active}>Active</Badge>
                <Badge color={Colors.positive}>Positive</Badge>
                <Badge color={Colors.negative}>Negative</Badge>
                <Badge size='sm' color={Colors.primary}>Small</Badge>
                <Badge color={Colors.primary}>Med</Badge>
                <Badge size='lg' color={Colors.primary}>Large</Badge>
                <Badge size='xl' color={Colors.primary}>XL</Badge>
            </Flexer>
        </Flexer>
    </a>;
};

const items: TypeaheadItem[] = [
    { id: 'orange', label: 'Orange', badge: '🍊' },
    { id: 'pineapple', label: 'Pineapple', badge: '🍍' },
    { id: 'blueberry', label: 'Blueberry', badge: '🫐' },
    { id: 'apple', label: 'Apple', badge: '🍏' },
    { id: 'banana', label: 'Banana', badge: '🍌' },
    { id: 'cherry', label: 'Cherry', badge: '🍒' },
    { id: 'grape', label: 'Grape', badge: '🍇' },
    { id: 'kiwi', label: 'Kiwi', badge: '🥝' },
    { id: 'lemon', label: 'Lemon', badge: '🍋' },
    { id: 'mango', label: 'Mango', badge: '🥭' },
    { id: 'nectarine', label: 'Nectarine', badge: '🍑' },
    { id: 'pear', label: 'Pear', badge: '🍐' },
    { id: 'strawberry', label: 'Strawberry', badge: '🍓' },
    { id: 'watermelon', label: 'Watermelon', badge: '🍉' },
];

const MenuDemo: React.FC<{ menuItems: TypeaheadItem[] }> = ({ menuItems }) => {
    return <Menu>
        <Menu.Section first title='Items'>
            {menuItems.map(x =>
                <div key={x.id}>
                    <Menu.Item>
                        <Menu.ItemText>{x.label}</Menu.ItemText>
                        <Menu.Badge color={Colors.positive}>{x.badge}</Menu.Badge>
                    </Menu.Item>
                </div>
            )}
        </Menu.Section>
    </Menu>;
};

const InputMenuDemo: React.FC = () => {
    const [value, setValue] = useState('');
    const initialItems: TypeaheadItem[] = [
        { id: 'orange', label: 'Orange', badge: '🍊' },
        { id: 'pineapple', label: 'Pineapple', badge: '🍍' },
        { id: 'blueberry', label: 'Blueberry', badge: '🫐' },
    ];

    const [selectedItems, setSelectedItems] = useState(initialItems);
    const existingLookup = useMemo(() => new Set(selectedItems.map(x => x.id)), [selectedItems]);

    const itemSource = (query: string) => {
        const filtered = items.filter(x => x.label.toLowerCase().includes(query.toLowerCase().trim()));
        return filtered.map(x => existingLookup.has(x.id) ? TypeaheadItem(x.id, x.label, 'Already Added', true) : x);
    };

    const onSelect = (item: TypeaheadItem) => {
        setSelectedItems([...selectedItems, item]);
        return true;
    };

    return <a id='inputs-section'>
        <Flexer flex='column' gap='0.5'>
            <Title size='xl'>Inputs:</Title>
            <Input placeholder='Placeholder' value={value} onChange={setValue} hint='This is a hint.' message='This is a message.' />
            <Input placeholder='Compact' value={value} onChange={setValue} compact={true} />
            <Input placeholder='Error' value={value} onChange={setValue} error={true} />
            <Input placeholder='Input' value={value} onChange={setValue} before='Block before string.' beforeVariant='block' />
            <Input placeholder='Input' value={value} onChange={setValue} before='Inline before string.' beforeVariant='inline' />
            <Input placeholder='Input' value={value} onChange={setValue} after='Block after string.' afterVariant='block' />
            <Input placeholder='Input' value={value} onChange={setValue} after='Inline after string.' afterVariant='inline' />
            <TypeaheadInput
                placeholder='Typeahead Input'
                typeaheadSource={itemSource}
                onTypeaheadSelect={onSelect}
            />
            <div style={{ overflow: 'hidden' }}>
                <MenuDemo menuItems={selectedItems} />
            </div>
            <div>
                <MenuButton label='Menu Button'>
                    <MenuDemo menuItems={selectedItems} />
                </MenuButton>
            </div>
        </Flexer>
    </a>;
};

const SkeletonDemo: React.FC = () => {
    return <a id='skeleton-section'>
        <Flexer flex='column'>
            <Title size='xl'>Skeleton:</Title>
            <Menu>
                <Menu.Section first title='Skeleton'>
                    <Menu.Item>
                        <Skeleton />
                    </Menu.Item>
                    <Menu.Item>
                        <Skeleton />
                    </Menu.Item>
                    <Menu.Item>
                        <Skeleton />
                    </Menu.Item>
                    <Menu.Item>
                        <Skeleton />
                    </Menu.Item>
                    <Menu.Item>
                        <Skeleton />
                    </Menu.Item>
                </Menu.Section>
            </Menu>
        </Flexer>
    </a>;
};

const SelectDemo: React.FC = () => {
    const [selected, setSelected] = useState(items[0]);

    return <a id='select-section'>
        <Title size='xl'>Select:</Title>
        <Select items={items} selected={selected} onChange={setSelected} />
    </a>;
};

const CommonSpinnerDemo: React.FC = () => {
    return <div style={{ padding: '0.5rem' }}>
        <CommonSpinner />
    </div>;
};

const SpinnerDemo: React.FC = () => {
    return <a id='spinner-section'>
        <Flexer flex='column' gap='1'>
            <Title size='xl'>Spinners:</Title>
            <Flexer flex='column' align='center' justify='space-between' gap='1.5'>
                <Spinner>
                    <div style={{ width: '100%', height: '100%', borderRadius: '50%', backgroundColor: '#555555' }} />
                </Spinner>
                <Spinner stroke={1} />
                <Spinner stroke={5} />
                <Spinner size={5} />
                <Loader
                    type="TailSpin"
                    color={Colors.text}
                    height={remInPx(2)}
                    width={remInPx(2)}
                />
                <LoadingGuard loading={true} />
                <CommonSpinnerDemo />
            </Flexer>
        </Flexer>
    </a>;
};

const DateTimeDemo: React.FC = () => {
    const [date, setDate] = useState(new Date(Date.now()));

    return <a id='date-time-section'>
        <Flexer flex='column'>
            <Title size='xl'>Date/Time Picker:</Title>
            <Flexer flex='column' align='center' gap='2'>
                <DatePopper date={date} onChange={setDate} />
                <TimePopper date={date} onChange={setDate} />
            </Flexer>
        </Flexer>
    </a>;
};

const CheckboxDemo: React.FC = () => {
    const [checked, setChecked] = useState(false);

    return <a id='checkbox-section'>
        <Flexer flex='column'>
            <Title size='xl'>Checkbox:</Title>
            <Flexer flex='column' align='center' justify='space-between' gap='1.5'>
                <Checkbox label='Checkbox' checked={checked} onChange={setChecked} />
                <Toggle size="sm" value={checked} onAction={setChecked} />
                <Toggle size="md" value={checked} onAction={setChecked} />
                <Check value={checked} />
            </Flexer>
        </Flexer>
    </a>;
};

const ColourBlock = styled.div<{ colour: string, border: boolean }>`
    background-color: ${props => props.colour};
    border: ${props => props.border ? '1px solid black' : 'none'};
    width: 100%;
    height: 1.5rem;
`;

const ColourDemo: React.FC = () => {
    return <a id='colour-section'>
        <Flexer flex='column'>
            <Title size='xl'>Colours:</Title>
            <Flexer flex='row' align='center' justify='space-around'>
                <Flexer flex='column' align='center' justify='space-between' height='25' width='20'>
                    <Flexer flex='row' width='100%'>
                        <div style={{ width: '100%' }}>Primary #3B65EF</div>
                        <ColourBlock colour={Colors.primary} border={false} />
                    </Flexer>
                    <Flexer flex='row' width='100%'>
                        <div style={{ width: '100%' }}>Primary Light #6584EA</div>
                        <ColourBlock colour={Colors.primaryLight} border={false} />
                    </Flexer>
                    <Flexer flex='row' width='100%'>
                        <div style={{ width: '100%' }}>Primary Dark #0b2886</div>
                        <ColourBlock colour={Colors.primaryDark} border={false} />
                    </Flexer>
                    <Flexer flex='row' width='100%'>
                        <div style={{ width: '100%' }}>Secondary #FFFFFF</div>
                        <ColourBlock colour={Colors.secondary} border={true} />
                    </Flexer>
                    <Flexer flex='row' width='100%'>
                        <div style={{ width: '100%' }}>Text #546077</div>
                        <ColourBlock colour={Colors.text} border={false} />
                    </Flexer>
                    <Flexer flex='row' width='100%'>
                        <div style={{ width: '100%' }}>Text Light #80899a</div>
                        <ColourBlock colour={Colors.textLight} border={false} />
                    </Flexer>
                    <Flexer flex='row' width='100%'>
                        <div style={{ width: '100%' }}>Text Lighter</div>
                        <ColourBlock colour={Colors.textLighter} border={false} />
                    </Flexer>
                    <Flexer flex='row' width='100%'>
                        <div style={{ width: '100%' }}>Text Lightest</div>
                        <ColourBlock colour={Colors.textLightest} border={false} />
                    </Flexer>
                    <Flexer flex='row' width='100%'>
                        <div style={{ width: '100%' }}>Text Dark</div>
                        <ColourBlock colour={Colors.textDark} border={false} />
                    </Flexer>
                </Flexer>
                <Flexer flex='column' align='center' justify='space-between' height='25' width='20'>
                    <Flexer flex='row' width='100%'>
                        <div style={{ width: '100%' }}>Border #e4e8ee</div>
                        <ColourBlock colour={Colors.border} border={false} />
                    </Flexer>
                    <Flexer flex='row' width='100%'>
                        <div style={{ width: '100%' }}>Primary Text #0f36b5</div>
                        <ColourBlock colour={Colors.primaryText} border={false} />
                    </Flexer>
                    <Flexer flex='row' width='100%'>
                        <div style={{ width: '100%' }}>Primary Text Lighter</div>
                        <ColourBlock colour={Colors.primaryTextLighter} border={false} />
                    </Flexer>
                    <Flexer flex='row' width='100%'>
                        <div style={{ width: '100%' }}>Primary Selected</div>
                        <ColourBlock colour={Colors.primarySelected} border={false} />
                    </Flexer>
                    <Flexer flex='row' width='100%'>
                        <div style={{ width: '100%' }}>Primary Hover</div>
                        <ColourBlock colour={Colors.primaryHover} border={false} />
                    </Flexer>
                    <Flexer flex='row' width='100%'>
                        <div style={{ width: '100%' }}>Primary Press</div>
                        <ColourBlock colour={Colors.primaryPress} border={false} />
                    </Flexer>
                    <Flexer flex='row' width='100%'>
                        <div style={{ width: '100%' }}>Hover #EEF2F6</div>
                        <ColourBlock colour={Colors.hover} border={false} />
                    </Flexer>
                    <Flexer flex='row' width='100%'>
                        <div style={{ width: '100%' }}>Active #DDE5ED</div>
                        <ColourBlock colour={Colors.active} border={false} />
                    </Flexer>
                </Flexer>
                <Flexer flex='column' align='center' justify='space-between' height='25' width='20'>
                    <Flexer flex='row' width='100%'>
                        <div style={{ width: '100%' }}>Positive #40B8A8</div>
                        <ColourBlock colour={Colors.positive} border={false} />
                    </Flexer>
                    <Flexer flex='row' width='100%'>
                        <div style={{ width: '100%' }}>Negative #F13B4D</div>
                        <ColourBlock colour={Colors.negative} border={false} />
                    </Flexer>
                    <Flexer flex='row' width='100%'>
                        <div style={{ width: '100%' }}>Observe #ff9f43</div>
                        <ColourBlock colour={Colors.observe} border={false} />
                    </Flexer>
                    <Flexer flex='row' width='100%'>
                        <div style={{ width: '100%' }}>Mystery #B974FF</div>
                        <ColourBlock colour={Colors.mystery} border={false} />
                    </Flexer>
                    <Flexer flex='row' width='100%'>
                        <div style={{ width: '100%' }}>Frame #BFBFBF</div>
                        <ColourBlock colour={Colors.frame} border={false} />
                    </Flexer>
                </Flexer>
            </Flexer>
        </Flexer>
    </a>;
};

const SliderDemo: React.FC = () => {
    const [value, setValue] = useState(50);

    return <a id='slider-section'>
        <Flexer flex='column' gap='1'>
            <Title size='xl'>Sliders:</Title>
            <Slider
                min={0}
                max={100}
                value={value}
                disabled={false}
                onChange={setValue}
            />
            <Slider
                min={0}
                max={100}
                value={50}
                disabled={true}
                onChange={setValue}
            />
            <SliderWithInput
                min={0}
                max={100}
                value={value}
                onChange={setValue}
            />
        </Flexer>
    </a>;
};

const LoadingBarDemo: React.FC = () => {
    const [progress, setProgress] = useState(0);

    useEffect(() => {
        const interval = setInterval(() => {
            setProgress(currentProgress => {
                if (currentProgress < 1) {
                    return currentProgress + 0.01;
                }
                return 0;
            });
        }, 100);

        return () => clearInterval(interval);
    }, []);

    return <a id='loading-bar-section'>
        <Flexer flex='column' gap='1'>
            <Title size='xl'>Loading Bar:</Title>
            <LoadingBar progress={progress} color={Colors.primary} />
        </Flexer>
    </a>;
};

const ColourSelectorDemo: React.FC = () => {
    const [colour, setColour] = useState('#f44336');

    return <a id='colour-selector-section'>
        <Flexer flex='column'>
            <Title size='xl'>Colour Selector:</Title>
            <Flexer flex='column' align='center'>
                <ColorSelector color={colour} groups onSelect={setColour} />
                <ColorButton value={colour} onChange={setColour} />
            </Flexer>
        </Flexer>
    </a>;
};

const FloatDemo: React.FC = () => {
    return <a id='float-section'>
        <Flexer flex='column'>
            <Title size='xl'>Float:</Title>
            <Flexer flex='column' align='center'>
                <Float width='36' height='45'>
                    <Flexer flex='column' justify='space-between' padding="1" height='44'>
                        <div>
                            <Title>Title:</Title>
                            <Copy flush>
                                Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed posuere sed risus ut egestas. Maecenas nulla magna, venenatis eget quam in, condimentum gravida felis. Proin malesuada lacinia elit, eu interdum sapien commodo vel. Praesent lacinia velit at faucibus efficitur. Aenean eget dolor eros. Cras dictum massa massa, non pretium nibh aliquet in. Ut non felis metus. Vestibulum semper maximus velit at dignissim. Nam congue orci vel bibendum efficitur.
                            </Copy>
                        </div>
                        <ButtonStrip>
                            <Button variant='primary'>Primary</Button>
                            <Button variant='positive'>Positive</Button>
                            <Button variant='negative'>Negative</Button>
                        </ButtonStrip>
                    </Flexer>
                </Float>
            </Flexer>
        </Flexer>
    </a>;
};

const GridDemo: React.FC = () => {
    return <a id='grid-section'>
        <Flexer flex='column'>
            <Title size='xl'>Grid:</Title>
            <Grid columns={3} columnGap="1" rowGap="1">
                {items.slice(0, 9).map(o =>
                    <Cell key={o.id} >
                        <GridButton onClick={null}>
                            <GridButton.Top color={Colors.primary}>
                                <GridButton.Icon color={Colors.primary}>
                                    <Icon size="1.5" src="deqo://icons/hero/bell" />
                                </GridButton.Icon>
                                <Title>{o.label}</Title>
                            </GridButton.Top>
                            <Copy>Grid Button</Copy>
                        </GridButton>
                    </Cell>
                )}
            </Grid>
        </Flexer>
    </a>;
};

const ResultInfoDemo: React.FC = () => {
    const [tick, setTick] = useState(0);

    useEffect(() => {
        const interval = setInterval(() => {
            setTick(currentTick => currentTick + 1);
        }, 5000);

        return () => clearInterval(interval);
    }, []);

    return <a id='result-info-section'>
        <Flexer flex='column'>
            <Title size='xl'>Result Info:</Title>
            <div>{tick}</div>
            <ResultInfo mode="positive" message="Result info component." />
            <ResultInfo mode="negative" message="Result info component." />
        </Flexer>
    </a>;
};

const InfoViewDemo: React.FC = () => {
    const { Section,
        LargeHeading, Heading, HeadingAction,
        DetailTable, DetailRow, DetailCell, DetailAction,
        ListItem, ListLabel, ListAction,
    } = InfoView;

    return <a id='info-view-section'>
        <Flexer flex='column'>
            <Title size='xl'>Info View:</Title>
            <InfoView>
                <Section>
                    <LargeHeading>Title</LargeHeading>
                </Section>
                <Section>
                    <Heading>
                        Information
                    </Heading>
                    <DetailTable>
                        <DetailRow>
                            <DetailCell shrink>Description:</DetailCell>
                            <DetailCell>Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed posuere sed risus ut egestas. Maecenas nulla magna, venenatis eget quam in, condimentum gravida felis.</DetailCell>
                        </DetailRow>
                        <DetailRow>
                            <DetailCell shrink>Status:</DetailCell>
                            <DetailCell>
                                <Badge size="lg" color={Colors.positive}>Active</Badge>
                            </DetailCell>
                        </DetailRow>
                    </DetailTable>
                </Section>
            </InfoView>
        </Flexer>
    </a>;
};

const NoticeDemo: React.FC = () => {
    return <a id='notice-section'>
        <Flexer flex='column' gap='1'>
            <Title size='xl'>Notice:</Title>
            <Notice icon="exclamation" text="This is a Notice." />
        </Flexer>
    </a>;
};

const InlineHelpDemo: React.FC = () => {
    return <a id='inline-help-section'>
        <Flexer flex='column' gap='1'>
            <Title size='xl'>Inline Help:</Title>
            <InlineHelp variant="primary">
                <Title>Inline Help (Primary)</Title>
                <Copy>
                    Lorem ipsum dolor sit amet.
                </Copy>
            </InlineHelp>
            <InlineHelp variant="secondary">
                <Title>Inline Help (Secondary)</Title>
                <Copy>
                    Lorem ipsum dolor sit amet.
                </Copy>
            </InlineHelp>
        </Flexer>
    </a>;
};

const PaymentCardDemo: React.FC = () => {
    return <a id='payment-card-section'>
        <Flexer flex='column' gap='1'>
            <Title size='xl'>Payment Cards:</Title>
            <Flexer flex='row' align='center' justify='space-between'>
                <PaymentCard type="master" digits="1234" expiry="12/24" />
                <PaymentCard type="visa" digits="5678" expiry="12/25" />
                <PaymentCard type="american_express" digits="9012" expiry="12/26" />
                <PaymentCard type="discover" digits="3456" expiry="12/27" />
            </Flexer>
        </Flexer>
    </a>;
};

const PillDemo: React.FC = () => {
    const [pillItems, setItems] = useState(items as PillableItem[]);

    return <a id='pill-section'>
        <Flexer flex='column' gap='1'>
            <Title size='xl'>Pills:</Title>
            <Flexer flex='row' align='center' justify='space-between'>
                <PillEditor items={pillItems} onUpdate={setItems} config={{
                    canEdit: true,
                    canRemove: true,
                    canAdd: true,
                    canSort: true,
                }} />
            </Flexer>
        </Flexer>
    </a>;
};

const PopArrowDemo: React.FC = () => {
    const arrowRef = useRef<HTMLDivElement>();

    return <PopArrow ref={arrowRef} position='top' color={Colors.mystery} />;
};

const TableDemo: React.FC = () => {
    return <a id="table-section">
        <Flexer flex='column' gap='1'>
            <Title size='xl'>Table:</Title>
            <Table>
                <Table.Head>
                    <Table.Row>
                        <Table.Cell>Label</Table.Cell>
                        <Table.Cell>Icon</Table.Cell>
                        <Table.Cell>Column</Table.Cell>
                        <Table.Cell>Column</Table.Cell>
                        <Table.Cell>Column</Table.Cell>
                    </Table.Row>
                </Table.Head>
                <Table.Body>
                    {items && items.map(x =>
                        <Table.Row key={x.id} clickable onClick={null}>
                            <Table.Cell>{x.label}</Table.Cell>
                            <Table.Cell><Badge color={Colors.border}>{x.badge}</Badge></Table.Cell>
                            <Table.Cell>Cell</Table.Cell>
                            <Table.Cell>Cell</Table.Cell>
                            <Table.Cell>Cell</Table.Cell>
                        </Table.Row>
                    )}
                </Table.Body>
            </Table>
        </Flexer>
    </a>;
};

const TipDemo: React.FC = () => {
    return <div style={{ width: '3rem', height: '1rem', backgroundColor: '#AAAAAA' }}>
        <Tip position="left" text="This is a tip.">
            <Icon src="deqo://icons/hero/check" size="3" />
        </Tip>
    </div>;
};

const VerticalTabDemo: React.FC = () => {
    return <a id='vertical-tab-section'>
        <Flexer flex='column' gap='1'>
            <Title size='xl'>Vertical Tab Set:</Title>
            <VerticalTabSet>
                <VerticalTabSet.Menu expand>
                    <VerticalTabSet.Link to="~/boards">
                        <Icon size="1.5" src="deqo://icons/hero/book-open" />Boards
                    </VerticalTabSet.Link>
                </VerticalTabSet.Menu>
                <VerticalTabSet.Content>
                    {items.map(x =>
                        <div key={x.id}>
                            <Menu.Item>
                                <Menu.ItemText>{x.label}</Menu.ItemText>
                                <Menu.Badge color={Colors.positive}>{x.badge}</Menu.Badge>
                            </Menu.Item>
                        </div>
                    )}
                </VerticalTabSet.Content>
            </VerticalTabSet>
        </Flexer>
    </a>;
};

const PopupTabsDemo: React.FC = () => {
    const [selected, setSelected] = useState(0);

    return <a id='popup-tabs-section'>
        <Flexer flex='column' gap='1'>
            <Title size='xl'>Popup Tabs:</Title>
            <PopupTabs padding=".5 .5 0" spacing=".5">
                {items.map(i =>
                    <Tip key={i.id} text={i.label} position="top">
                        <PopupTabs.Item onClick={() => setSelected(items.indexOf(i))} selected={items.indexOf(i) === selected}>
                            <Menu.Badge color={Colors.positive}>{i.badge}</Menu.Badge>
                        </PopupTabs.Item>
                    </Tip>
                )}
            </PopupTabs>
        </Flexer>
    </a>;
};

export const DemoPage = () =>
    <Flexer flex="row">
        <Flexer width="12" padding="1" height="100vh" borderRight={`.125rem solid ${Colors.border}`}>
            <Menu>
                <a href="#buttons-section"><Menu.Item>Buttons</Menu.Item></a>
                <a href="#button-toggle-section"><Menu.Item>Button Toggle</Menu.Item></a>
                <a href="#star-rating-section"><Menu.Item>Star Rating</Menu.Item></a>
                <a href="#badges-section"><Menu.Item>Badges</Menu.Item></a>
                <a href="#inputs-section"><Menu.Item>Inputs</Menu.Item></a>
                <a href="#select-section"><Menu.Item>Select</Menu.Item></a>
                <a href="#skeleton-section"><Menu.Item>Skeleton</Menu.Item></a>
                <a href="#checkbox-section"><Menu.Item>Checkbox</Menu.Item></a>
                <a href="#date-time-section"><Menu.Item>Date/Time Picker</Menu.Item></a>
                <a href="#spinner-section"><Menu.Item>Spinner</Menu.Item></a>
                <a href="#float-section"><Menu.Item>Float</Menu.Item></a>
                <a href="#slider-section"><Menu.Item>Sliders</Menu.Item></a>
                <a href="#colour-section"><Menu.Item>Colours</Menu.Item></a>
                <a href="#colour-selector-section"><Menu.Item>Colour Selector</Menu.Item></a>
                <a href="#grid-section"><Menu.Item>Grid</Menu.Item></a>
                <a href="#info-view-section"><Menu.Item>Info View</Menu.Item></a>
                <a href="#result-info-section"><Menu.Item>Result Info</Menu.Item></a>
                <a href="#loading-bar-section"><Menu.Item>Loading Bar</Menu.Item></a>
                <a href="#inline-help-section"><Menu.Item>Inline Help</Menu.Item></a>
                <a href="#notice-section"><Menu.Item>Notice</Menu.Item></a>
                <a href="#payment-card-section"><Menu.Item>Payment Cards</Menu.Item></a>
                <a href="#pill-section"><Menu.Item>Pills</Menu.Item></a>
                <a href="#table-section"><Menu.Item>Table</Menu.Item></a>
                <a href="#vertical-tab-section"><Menu.Item>Vertical Tabs</Menu.Item></a>
                <a href="#popup-tabs-section"><Menu.Item>Popup Tabs</Menu.Item></a>
            </Menu>
        </Flexer>
        <Flexer grow>
            <Scroller>
                <Flexer flex="column" gap="2" padding="2 2 15 2">
                    <ButtonDemo />
                    <ButtonToggleDemo />
                    <StarRatingDemo />
                    <BadgeDemo />
                    <InputMenuDemo />
                    <SelectDemo />
                    <SkeletonDemo />
                    <CheckboxDemo />
                    <DateTimeDemo />
                    <SpinnerDemo />
                    <FloatDemo />
                    <SliderDemo />
                    <ColourDemo />
                    <ColourSelectorDemo />
                    <GridDemo />
                    <InfoViewDemo />
                    <ResultInfoDemo />
                    <LoadingBarDemo />
                    <InlineHelpDemo />
                    <NoticeDemo />
                    <PaymentCardDemo />
                    <PillDemo />
                    <TableDemo />
                    <VerticalTabDemo />
                    <PopupTabsDemo />
                </Flexer>
            </Scroller>
        </Flexer>
    </Flexer>;
